import { Button } from '@nespresso-design-system/components';
import PriceContainer from '@nespresso/catalog-components-react/containers/PriceContainer';
import { namespace } from '@/widgets/ProductList/Providers';
import {
  strikethroughPriceFromProductDiscount,
  type Product
} from '@/domain/Product';
import styles from './QuickViewFooter.module.scss';
import PricingDetails from '@/widgets/ProductList/cards/sections/ProductPurchaseSection/parts/PricingDetails/PricingDetails';
import useMarketConfig from '@/widgets/hooks/useMarketConfig';
import useRoute from '@/widgets/hooks/useRoute';
import useTranslations from '@/widgets/hooks/useTranslations';
import type { Translations } from '@/widgets/ProductList/config';
import usePriceFormatter from '@/widgets/hooks/usePriceFormatter';
import useWidgetOptions from '@/widgets/hooks/useWidgetOptions';
import type { Options } from '../../../config/index.ts';
import AddToCartButtonDisplayer from '@/widgets/components/controls/AddToCartButtonDisplayer/AddToCartButtonDisplayer.tsx';
import useABTestVariant from '@/widgets/hooks/useABTestVariant.tsx';
import EXPERIMENTS from '@/constants/abExperiments.ts';

const namespaceQuickView = `${namespace}QuickView`;

export type QuickViewFooterProps = {
  product: Product;
  onGoToProductPage: (event: MouseEvent) => void;
};
export default function QuickViewFooter({
  product,
  onGoToProductPage
}: QuickViewFooterProps) {
  const formatPrice = usePriceFormatter();
  const { locale } = useRoute();
  const isAlternateVariant = useABTestVariant(
    EXPERIMENTS.QUICK_VIEW_DRAWER.ALTERNATE
  );
  const isControlVariant = !isAlternateVariant;
  const {
    financialSettings: { defaultCurrency: currencyCode, pricingModel }
  } = useMarketConfig();
  const { t } = useTranslations<Translations>();
  const { displayNewAddToBagButton } = useWidgetOptions<Options>();

  return (
    <div class={styles.container}>
      <div class={styles.priceContainer}>
        <PriceContainer
          locale={locale}
          currencyCode={currencyCode}
          price={product.pricing.price}
          strikethroughPrice={
            product.discount
              ? strikethroughPriceFromProductDiscount(
                  product.pricing.price,
                  product.discount
                )
              : undefined
          }
          layoutMode="row"
          layoutOrder="price-first"
          priceFormatter={(v) => formatPrice(v)}
        />
        {/* TODO: This should be a child of PriceContainer */}
        <PricingDetails
          product={product}
          pricingModel={pricingModel}
          class={styles.capsuleLabel}
        />
      </div>
      <div class={styles.ctas}>
        {/* A/B Test variant */}
        {isControlVariant && (
          <Button
            label={t('productList.quickView.learnMoreBtnLabel')}
            variant="outline"
            onClick={onGoToProductPage}
          />
        )}
        <AddToCartButtonDisplayer
          displayNewAddToBagButton={displayNewAddToBagButton}
          product={product}
          class={styles.addToCart}
          quantityPickerPosition="top"
          namespace={namespaceQuickView}
        />
      </div>
    </div>
  );
}
